import { default as styled } from '@emotion/styled'
import { faCircle as faCircleRegular } from '@fortawesome/free-regular-svg-icons'
import { faCircle, faLocationDot } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import axios from 'axios'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactJoyride from 'react-joyride'
import Breakpoint from '../../Breakpoint'
import { SERVER_URL, apiHeader } from '../../config'
import UserContext from '../../context/UserContext'
import { useLangSelected } from '../../hooks/useLangSelected'
import { PlanningData } from '../../types/planning.type'
import catchError from '../global/CatchError'
import Loader from '../global/Loader'
import Section from '../global/Section'
import Title from '../global/Title'
import PlanningContent from './PlanningContent'
import PlanningHeader from './PlanningHeader'

type Props = {
    joyRideState: ReactJoyride['props']
    setJoyRideState: (v: ReactJoyride['props']) => void
    setPlanningNames: (names: string[]) => void
}

export const generatePlanningId = (name: string): string => {
    const slug = name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
    return `planning-${slug}`
}

const Planning = ({ joyRideState, setJoyRideState, setPlanningNames }: Props) => {
    const user = useContext(UserContext)

    const lng = useLangSelected()

    const { t } = useTranslation('translation')

    const [plannings, setPlannings] = useState<PlanningData[]>([])
    const [loading, setLoading] = useState(true)

    const loadPlannings = () => {
        axios
            .post(`${SERVER_URL}/plannings/active`, { lng }, apiHeader())
            .then(({ data }) => {
                if (!data || !Array.isArray(data)) {
                    setPlannings([])
                    setPlanningNames([])
                    setLoading(false)
                    return
                }

                setPlannings(data)
                setPlanningNames(data.map((p: PlanningData) => p.name))

                // Configure Joyride steps based on plannings order
                if (data.length > 0) {
                    const steps = [...joyRideState.steps]

                    data.forEach((planning: PlanningData, index: number) => {
                        const stepIndex = 2 + index
                        const planningId = generatePlanningId(planning.name)
                        const targetId = `#${planningId}`

                        steps[stepIndex] = {
                            disableBeacon: true,
                            target: targetId,
                            title: t(`page.planning.joyride.planning.title`),
                            content: t(`page.planning.joyride.planning.content`),
                            placement: 'auto',
                            showProgress: true,
                            showSkipButton: true,
                        }
                    })

                    setJoyRideState({ ...joyRideState, steps })
                }

                setLoading(false)
            })
            .catch((err) => {
                catchError(err.response?.data || err)
                setPlannings([])
                setPlanningNames([])
                setLoading(false)
            })
    }

    useEffect(() => {
        loadPlannings()
    }, [user.tokenAdmin, lng])

    if (loading) return <Loader />

    return (
        <>
            {plannings.map((planningData, index) => {
                const planningId = generatePlanningId(planningData.name)
                const showCompetitorLegend = index === plannings.length - 1

                return (
                    <div key={planningId} id={planningId} data-testid="schedule-section">
                        <Header>
                            <div>
                                <Title>{planningData.name}</Title>
                                {planningData.name.toLowerCase().includes('été') && (
                                    <PlanningSubTitle>
                                        {t('page.planning.summer-sessions')} <br />
                                    </PlanningSubTitle>
                                )}
                            </div>
                            <Legend>
                                <TitleLegend>Légende</TitleLegend>

                                <div>
                                    <PrimaryIcon icon={faCircle} /> Salle du blason{' '}
                                    <a
                                        href="https://www.google.com/maps/place/Salle+Blason/@50.6253722,3.1212522,17z/data=!3m1!4b1!4m5!3m4!1s0x47c2d63d6dc9e9af:0x370d617a274544fc!8m2!3d50.6253745!4d3.1234539"
                                        target="_blank"
                                    >
                                        <DotPosition icon={faLocationDot} />
                                    </a>
                                </div>
                                <div>
                                    <WhiteIcon icon={faCircleRegular} color="white" /> Complexe des ESUM{' '}
                                    <a
                                        href="https://www.google.com/maps/place/Espace+Sportif+Universitaire+Municipale/@50.6297379,3.1216652,17z/data=!3m1!4b1!4m5!3m4!1s0x47c2d62289a8882b:0x17d9586a355b951e!8m2!3d50.6298932!4d3.1236674"
                                        target="_blank"
                                    >
                                        <DotPosition icon={faLocationDot} />
                                    </a>
                                </div>

                                {showCompetitorLegend && (
                                    <CompetitorLegendText
                                        style={{
                                            marginTop: '16px',
                                        }}
                                    >
                                        🏆 Séance Compétiteur{' '}
                                    </CompetitorLegendText>
                                )}
                            </Legend>
                        </Header>

                        <PlanningContainer>
                            <PlanningHeader planning={planningData} />
                            <PlanningContent planning={planningData} />
                        </PlanningContainer>
                    </div>
                )
            })}
        </>
    )
}

const CompetitorLegendText = styled.div`
    margin-top: 16px;
`

const TitleLegend = styled.div`
    font-size: 2rem;
    color: var(--primary-color);
    margin-bottom: 8px;
`

const DotPosition = styled(FontAwesomeIcon)`
    height: 25px;
`

export const PlanningSubTitle = styled.div`
    margin-bottom: 16px;
    font-size: 1.8rem;
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        flex-direction: column;
    }
`

const PrimaryIcon = styled(FontAwesomeIcon)`
    color: var(--primary-color);
`

const WhiteIcon = styled(FontAwesomeIcon)`
    color: var(--primary-color);
`

const Legend = styled(Section)`
    display: flex;
    flex-direction: column;
    width: max-content;
    margin-bottom: 16px;

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        width: 100%;
    }
`

const PlanningContainer = styled.div`
    width: 1800px;
    background-color: white;
    border-radius: 8px;
    margin-bottom: 32px;
`

export default Planning
